import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroHomeBg from "@assets/stock_images/hero_home_1920.webp";
import { HeroImage } from "@/components/hero-image";
import {
  Truck, Zap, Clock, MapPin, Phone, Shield, Users, Package,
  Factory, Wrench, Store, ChevronRight, CheckCircle2, ArrowRight
} from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { getWhatsAppUrl, PHONE_HREF, PHONE_NUMBER, trackWhatsAppClick, trackPhoneClick } from "@/lib/tracking";
import QuickForm from "@/components/quick-form";
import { motion } from "framer-motion";
import Seo from "@/components/seo";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

export default function Home() {
  return (
    <div>
      <Seo
        title="Agencia de Transporte en Yecla | Urgente y Estándar | TDMA Logística"
        description="TDMA Logística, agencia de transporte en Yecla. Envíos urgentes con recogida en cualquier provincia y servicio estándar de paquetería y pallets a 0,37 €/km."
        keywords="agencia de transporte yecla, empresa de transporte yecla, transportes yecla, transporte urgente yecla, transporte pallets yecla, paquetería yecla, transporte nacional yecla, agencia transporte murcia"
        canonical="https://tdmalogistica.com"
      />
      <section className="relative text-white overflow-hidden">
        <HeroImage src={heroHomeBg} />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(150,50%,18%)] via-[hsl(150,45%,22%)] to-[hsl(155,40%,15%)] opacity-[0.82]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <Zap className="w-4 h-4 text-yellow-300" />
                Recogida rápida en toda España
              </div>

              <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] leading-tight mb-5" data-testid="text-hero-title">
                Transporte urgente y estándar en{" "}
                <span className="text-emerald-300">toda España</span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed" data-testid="text-hero-subtitle">
                Recogida rápida en cualquier provincia. Tarifa desde{" "}
                <span className="font-semibold text-white">0,37 €/km</span> para paquetería y pallets.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link href="/contacto">
                  <Button size="lg" className="bg-white text-[hsl(150,45%,22%)] font-semibold w-full sm:w-auto" data-testid="button-hero-presupuesto">
                    Pedir Presupuesto
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
                <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick("hero")}>
                  <Button size="lg" variant="outline" className="border-white/30 text-white bg-white/10 w-full sm:w-auto" data-testid="button-hero-whatsapp">
                    <SiWhatsapp className="w-5 h-5 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a href={PHONE_HREF} onClick={() => trackPhoneClick("hero")}>
                  <Button size="lg" variant="outline" className="border-white/30 text-white bg-white/10 w-full sm:w-auto" data-testid="button-hero-llamar">
                    <Phone className="w-5 h-5 mr-2" />
                    Llamar
                  </Button>
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-white/60">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Sin compromiso</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Respuesta inmediata</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Cobertura nacional</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <Card className="p-6 bg-background text-foreground shadow-2xl">
                <h2 className="font-display font-semibold text-lg mb-4" data-testid="text-hero-form-title">
                  Solicita tu presupuesto
                </h2>
                <QuickForm source="home" />
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="text-services-title">Nuestros Servicios</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Dos soluciones de transporte adaptadas a tus necesidades, con cobertura en toda España.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div {...fadeInUp}>
              <Card className="p-6 sm:p-8 h-full flex flex-col hover-elevate">
                <div className="w-12 h-12 rounded-md bg-destructive/10 flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2" data-testid="text-service-urgente">Servicio Urgente</h3>
                <p className="text-3xl font-bold text-primary mb-1">0,37 €<span className="text-base font-normal text-muted-foreground">/km</span></p>
                <p className="text-muted-foreground mb-4 flex-1">
                  Recogida rápida en cualquier provincia de España. Ideal para incidencias, reposiciones urgentes y piezas críticas.
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Recogida el mismo día</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Entrega urgente puerta a puerta</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Seguimiento en tiempo real</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Cobertura nacional completa</li>
                </ul>
                <Link href="/servicio-urgente">
                  <Button className="w-full" data-testid="button-service-urgente">
                    Ver Servicio Urgente
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ duration: 0.5, delay: 0.1 }}>
              <Card className="p-6 sm:p-8 h-full flex flex-col hover-elevate">
                <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                  <Truck className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2" data-testid="text-service-estandar">Servicio Estándar</h3>
                <p className="text-3xl font-bold text-primary mb-1">0,37 €<span className="text-base font-normal text-muted-foreground">/km</span></p>
                <p className="text-muted-foreground mb-4 flex-1">
                  Rutas habituales y envíos recurrentes. La mejor tarifa para paquetería y pallets en toda España.
                </p>
                <ul className="space-y-2 mb-6 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Rutas optimizadas</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Envíos recurrentes</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Paquetería y pallets</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> Precios competitivos</li>
                </ul>
                <Link href="/servicio-estandar">
                  <Button variant="outline" className="w-full" data-testid="button-service-estandar">
                    Ver Servicio Estándar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="text-howit-works">¿Cómo funciona?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              En 3 sencillos pasos gestionamos tu envío de principio a fin.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "1", icon: Phone, title: "Solicitud", desc: "Rellena el formulario, llámanos o escríbenos por WhatsApp con los datos de tu envío." },
              { step: "2", icon: CheckCircle2, title: "Confirmación", desc: "Te confirmamos precio, horario de recogida y plazo de entrega de forma inmediata." },
              { step: "3", icon: Truck, title: "Recogida y Entrega", desc: "Recogemos en origen y entregamos en destino. Seguimiento completo de tu envío." },
            ].map((item, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <div className="text-center">
                  <div className="relative w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-primary" />
                    <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">¿A quién va dirigido?</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Trabajamos con todo tipo de empresas que necesitan transporte fiable y rápido.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { icon: Factory, label: "Fabricantes" },
              { icon: Store, label: "Distribuidores" },
              { icon: Wrench, label: "Talleres" },
              { icon: Package, label: "E-commerce" },
              { icon: Users, label: "Empresas" },
            ].map((item, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ duration: 0.4, delay: i * 0.05 }}>
                <Card className="p-4 sm:p-5 text-center hover-elevate">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">¿Por qué TDMA Logistica?</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Rapidez", desc: "Recogida inmediata y entrega en el menor tiempo posible." },
              { icon: MapPin, title: "Cobertura Nacional", desc: "Operamos en todas las provincias de España sin excepción." },
              { icon: Users, title: "Trato Directo", desc: "Coordinación personalizada, sin intermediarios ni centralitas." },
              { icon: Shield, title: "Confianza", desc: "Compromiso con los plazos y la seguridad de tu mercancía." },
            ].map((item, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ duration: 0.4, delay: i * 0.08 }}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background lg:hidden">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <motion.div {...fadeInUp}>
            <h2 className="font-display font-bold text-2xl mb-6 text-center">Solicita tu presupuesto</h2>
            <Card className="p-6">
              <QuickForm source="home" />
            </Card>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-muted/30 lg:bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-8">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="text-faq-title">Preguntas Frecuentes</h2>
          </motion.div>

          <motion.div {...fadeInUp}>
            <Accordion type="single" collapsible className="space-y-2" data-testid="accordion-faq-home">
              {[
                { q: "¿Cuánto cuesta el servicio de transporte?", a: "Nuestra tarifa parte de 0,37 €/km tanto para el servicio urgente como para el estándar. El precio final depende de la distancia, el tipo de carga y las condiciones del envío. Solicita tu presupuesto sin compromiso." },
                { q: "¿Cuánto tarda la recogida en el servicio urgente?", a: "En el servicio urgente, coordinamos la recogida lo más rápido posible, normalmente el mismo día de la solicitud en función de la ubicación y la hora del pedido." },
                { q: "¿Qué tipo de mercancía podéis transportar?", a: "Transportamos paquetería, pallets y cargas parciales. Si tienes una necesidad especial, consúltanos y te daremos una solución adaptada." },
                { q: "¿Cómo puedo solicitar un presupuesto?", a: "Puedes rellenar el formulario de esta web, llamarnos, escribirnos por WhatsApp o enviarnos un email. Te responderemos de inmediato." },
              ].map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border rounded-md px-4">
                  <AccordionTrigger className="text-left text-sm font-medium" data-testid={`trigger-faq-home-${i}`}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-gradient-to-br from-[hsl(150,50%,18%)] via-[hsl(150,45%,22%)] to-[hsl(155,40%,15%)] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-4">
              ¿Necesitas un transporte? Háblanos ahora
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Solicita tu presupuesto sin compromiso. Te respondemos de forma inmediata por el canal que prefieras.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link href="/contacto">
                <Button size="lg" className="bg-white text-[hsl(150,45%,22%)] font-semibold w-full sm:w-auto" data-testid="button-final-cta">
                  Pedir Presupuesto
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick("footer-cta")}>
                <Button size="lg" variant="outline" className="border-white/30 text-white bg-white/10 w-full sm:w-auto" data-testid="button-final-whatsapp">
                  <SiWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a href={PHONE_HREF} onClick={() => trackPhoneClick("footer-cta")}>
                <Button size="lg" variant="outline" className="border-white/30 text-white bg-white/10 w-full sm:w-auto" data-testid="button-final-llamar">
                  <Phone className="w-5 h-5 mr-2" />
                  {PHONE_NUMBER}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
